import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Grid, Paper, Snackbar, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import MultiSelectInput from '@/se/components/inputs/MultiSelectInput';
import Spinner from '@/se/components/Spinner';
import EntityErrorState from '@/se/components/entity-states/EntityErrorState';
import { delayAlertEligibleRecipients as eligibleRecipientsQuery } from '../../../graph/hospitals';
import configScheme from '../../../graph/config';
import { ConfigType } from '../../../types/HospitalConfig';

const listQuery = configScheme.list;

const parseConfigValues = raw => {
  if (!raw) return { waitingRoomThresholdMinutes: '', preopThresholdMinutes: '', recipients: [] };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return {
      waitingRoomThresholdMinutes: parsed.waitingRoomThresholdMinutes ?? '',
      preopThresholdMinutes: parsed.preopThresholdMinutes ?? '',
      recipients: parsed.recipients ?? [],
    };
  } catch {
    return { waitingRoomThresholdMinutes: '', preopThresholdMinutes: '', recipients: [] };
  }
};

const DelayAlertConfig = () => {
  const { data, loading, error } = useQuery(listQuery, {
    variables: { type: ConfigType.DelayAlertConfig },
  });

  const { data: recipientsData, loading: recipientsLoading, error: recipientsError } = useQuery(eligibleRecipientsQuery);

  useEffect(() => {
    if (recipientsError) {
      console.error('[DelayAlertConfig] Failed to load eligible recipients:', recipientsError);
    }
  }, [recipientsError]);

  const [createConfig, { loading: creating }] = useMutation(configScheme.create);
  const [updateConfig, { loading: updating }] = useMutation(configScheme.update);
  const [deleteConfig, { loading: deleting }] = useMutation(configScheme.remove);

  const saving = creating || updating;

  const existingConfig = data?.configs?.[0] ?? null;

  const [waitingRoom, setWaitingRoom] = useState('');
  const [preop, setPreop] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  useEffect(() => {
    if (existingConfig) {
      const config = parseConfigValues(existingConfig.values);
      setWaitingRoom(config.waitingRoomThresholdMinutes);
      setPreop(config.preopThresholdMinutes);
      setSelectedRecipients(config.recipients);
    }
  }, [existingConfig]);

  const recipientOptions = useMemo(() => {
    if (!recipientsData?.delayAlertEligibleRecipients) return [];
    return recipientsData.delayAlertEligibleRecipients.map(user => ({
      value: user.id,
      label: user.name,
    }));
  }, [recipientsData]);

  const selectedValues = useMemo(
    () => selectedRecipients.map(id => recipientOptions.find(opt => opt.value === id)).filter(Boolean),
    [selectedRecipients, recipientOptions]
  );

  const handleRecipientsChange = options => {
    setSelectedRecipients(options ? options.map(opt => opt.value) : []);
  };

  const refetchConfig = {
    refetchQueries: [{ query: listQuery, variables: { type: ConfigType.DelayAlertConfig } }],
    // Keep `saving` true until the list refetch lands so `existingConfig` is current
    // before Save re-enables; otherwise a follow-up save can race and re-create.
    awaitRefetchQueries: true,
  };

  const buildValues = () =>
    JSON.stringify({
      waitingRoomThresholdMinutes: waitingRoom === '' || waitingRoom == null ? null : Number(waitingRoom),
      preopThresholdMinutes: preop === '' || preop == null ? null : Number(preop),
      recipients: selectedRecipients,
    });

  const handleSave = async () => {
    const values = buildValues();

    try {
      if (existingConfig) {
        await updateConfig({ variables: { id: existingConfig.id, values }, ...refetchConfig });
      } else {
        await createConfig({ variables: { type: ConfigType.DelayAlertConfig, values }, ...refetchConfig });
      }
      setSnackbar({ open: true, severity: 'success', message: 'Delay alert configuration saved.' });
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message || 'Failed to save configuration.' });
    }
  };

  const handleDisable = async () => {
    if (!existingConfig) return;
    try {
      await deleteConfig({ variables: { id: existingConfig.id }, ...refetchConfig });
      setWaitingRoom('');
      setPreop('');
      setSelectedRecipients([]);
      setSnackbar({ open: true, severity: 'success', message: 'Delay alerts disabled.' });
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message || 'Failed to disable alerts.' });
    }
  };

  if (loading) return <Spinner />;
  if (error) return <EntityErrorState error={error} />;

  return (
    <>
      <Box mb={4}>
        <Typography variant="h1">Delay Alert Configuration</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8} lg={6}>
          <Paper>
            <Box p={3}>
              <Typography variant="subtitle1" gutterBottom>
                Configure threshold times for patient delay notifications. When a patient exceeds the configured time in
                a waiting room or pre-op area, alert notifications will be sent to the selected recipients via SMS.
              </Typography>

              <Box mt={3}>
                <Typography variant="body2" style={{ marginBottom: 8, opacity: 0.7 }}>
                  Waiting Room Threshold (minutes)
                </Typography>
                <TextField
                  type="number"
                  variant="filled"
                  fullWidth
                  placeholder="Leave empty to disable waiting room alerts"
                  value={waitingRoom}
                  onChange={e => {
                    const val = e.target.value;
                    setWaitingRoom(val === '' ? '' : Number(val));
                  }}
                  inputProps={{ min: 1 }}
                  onWheel={e => e.target.blur()}
                />
              </Box>

              <Box mt={3}>
                <Typography variant="body2" style={{ marginBottom: 8, opacity: 0.7 }}>
                  Pre-Op Threshold (minutes)
                </Typography>
                <TextField
                  type="number"
                  variant="filled"
                  fullWidth
                  placeholder="Leave empty to disable pre-op alerts"
                  value={preop}
                  onChange={e => {
                    const val = e.target.value;
                    setPreop(val === '' ? '' : Number(val));
                  }}
                  inputProps={{ min: 1 }}
                  onWheel={e => e.target.blur()}
                />
              </Box>

              <Box mt={3}>
                <Typography variant="body2" style={{ marginBottom: 8, opacity: 0.7 }}>
                  Notification Recipients
                </Typography>
                <MultiSelectInput
                  name="delayAlertRecipients"
                  options={recipientOptions}
                  onChange={handleRecipientsChange}
                  value={selectedValues}
                  loading={recipientsLoading}
                  placeholder="Select recipients..."
                />
                <Typography variant="caption" style={{ opacity: 0.5, marginTop: 4, display: 'block' }}>
                  Only users with a phone number configured are shown.
                </Typography>
              </Box>

              <Box mt={4} display="flex" style={{ gap: 12 }}>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
                {existingConfig && (
                  <Button variant="outlined" onClick={handleDisable} disabled={deleting}>
                    {deleting ? <CircularProgress size={20} /> : 'Disable Alerts'}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DelayAlertConfig;
